/**
 * Modules transverses du modèle v6.0 — paiement, notification multi-canal,
 * assistance IA documentaire, tickets.
 * Source : `db/migrations/002_modules_transverses.sql`.
 */
import { valeurs } from './enumeration.js';
// -----------------------------------------------------------------------------
// M-PAY — paiement des titres
// -----------------------------------------------------------------------------
/**
 * `TARIF.categorie_embarcation` — reprend les catégories d'embarcation, plus
 * `toutes` pour un barème qui ne segmente pas.
 */
export const CATEGORIE_TARIF = {
    NAVIRE: 'navire',
    PIROGUE: 'pirogue',
    TOUTES: 'toutes',
};
export const CATEGORIES_TARIF = valeurs(CATEGORIE_TARIF);
/**
 * `AVIS_PAIEMENT.statut`. La délivrance d'un titre est bloquée tant que l'avis
 * lié n'est pas `paye` (cf. `@sygap/core` — règles de délivrance).
 */
export const STATUT_AVIS_PAIEMENT = {
    EMIS: 'emis',
    PARTIEL: 'partiel',
    PAYE: 'paye',
    ANNULE: 'annule',
    EXPIRE: 'expire',
};
export const STATUTS_AVIS_PAIEMENT = valeurs(STATUT_AVIS_PAIEMENT);
/** `TRANSACTION_PAIEMENT.canal`. */
export const CANAL_PAIEMENT = {
    MOBILE_MONEY: 'mobile_money',
    VIREMENT_BANCAIRE: 'virement_bancaire',
    ESPECES_TRESOR: 'especes_tresor',
    CHEQUE: 'cheque',
};
export const CANAUX_PAIEMENT = valeurs(CANAL_PAIEMENT);
/** `TRANSACTION_PAIEMENT.statut`. */
export const STATUT_TRANSACTION = {
    INITIEE: 'initiee',
    EN_ATTENTE: 'en_attente',
    CONFIRMEE: 'confirmee',
    ECHOUEE: 'echouee',
    REMBOURSEE: 'remboursee',
};
export const STATUTS_TRANSACTION = valeurs(STATUT_TRANSACTION);
/** `JOURNAL_PAIEMENT.type_evenement` — journal INSERT ONLY des recettes. */
export const EVENEMENT_PAIEMENT = {
    INITIATION: 'INITIATION',
    RAPPEL_OPERATEUR: 'RAPPEL_OPERATEUR',
    CONFIRMATION: 'CONFIRMATION',
    ECHEC: 'ECHEC',
    RAPPROCHEMENT: 'RAPPROCHEMENT',
    ANNULATION: 'ANNULATION',
    REMBOURSEMENT: 'REMBOURSEMENT',
};
export const EVENEMENTS_PAIEMENT = valeurs(EVENEMENT_PAIEMENT);
/** Devise par défaut des barèmes et avis (`DEFAULT 'GNF'` en base). */
export const DEVISE_PAR_DEFAUT = 'GNF';
// -----------------------------------------------------------------------------
// M-NOTIF — notification multi-canal
// -----------------------------------------------------------------------------
/** `CANAL_NOTIFICATION.code`. */
export const CODE_CANAL_NOTIFICATION = {
    IN_APP: 'IN_APP',
    EMAIL: 'EMAIL',
    SMS: 'SMS',
    WHATSAPP: 'WHATSAPP',
};
export const CODES_CANAL_NOTIFICATION = valeurs(CODE_CANAL_NOTIFICATION);
/**
 * `MODELE_MESSAGE.statut_approbation` — un modèle WhatsApp doit être approuvé
 * par Meta avant tout envoi.
 */
export const STATUT_APPROBATION_MODELE = {
    NON_REQUIS: 'non_requis',
    BROUILLON: 'brouillon',
    SOUMIS: 'soumis',
    APPROUVE: 'approuve',
    REJETE: 'rejete',
};
export const STATUTS_APPROBATION_MODELE = valeurs(STATUT_APPROBATION_MODELE);
/** `ENVOI_NOTIFICATION.statut`. */
export const STATUT_ENVOI = {
    EN_FILE: 'en_file',
    ENVOYE: 'envoye',
    DELIVRE: 'delivre',
    LU: 'lu',
    ECHEC: 'echec',
    ABANDONNE: 'abandonne',
};
export const STATUTS_ENVOI = valeurs(STATUT_ENVOI);
/**
 * `MODELE_MESSAGE.code_evenement` — événements métier qui donnent lieu à
 * notification, pré-chargés par la migration 006.
 *
 * Aucune contrainte `CHECK` ne les borne en base : le catalogue est ouvert par
 * construction, comme `TYPE_AUTORISATION`. Ce qui est fermé, c'est la liste
 * des événements que **le code émet** — un service qui inventerait un code
 * absent d'ici n'aurait aucun modèle à rendre, et le titulaire resterait muet.
 * Les nommer ici est ce qui permet à l'API, à l'interface d'administration des
 * modèles et à la migration de désigner la même chose.
 */
export const CODE_EVENEMENT_NOTIFICATION = {
    /** Le dossier est déposé et porte sa référence officielle. */
    DEMANDE_DEPOSEE: 'DEMANDE_DEPOSEE',
    /** Une administration du circuit a rendu un avis favorable. */
    DEMANDE_ETAPE_VALIDEE: 'DEMANDE_ETAPE_VALIDEE',
    /** Une administration a rendu un avis défavorable : le dossier sort du circuit. */
    DEMANDE_REJETEE: 'DEMANDE_REJETEE',
    /** Le titre est signé et le document est retirable. */
    TITRE_DELIVRE: 'TITRE_DELIVRE',
    TITRE_SUSPENDU: 'TITRE_SUSPENDU',
    TITRE_ANNULE: 'TITRE_ANNULE',
    TITRE_RETABLI: 'TITRE_RETABLI',
    /** Première alerte d'échéance, à J-60 (cadrage §4.4). */
    TITRE_EXPIRATION_PROCHE: 'TITRE_EXPIRATION_PROCHE',
    /** Seconde alerte, à J-30. */
    TITRE_EXPIRATION_IMMINENTE: 'TITRE_EXPIRATION_IMMINENTE',
    /** Un avis de paiement est émis : la redevance est à régler (US-042). */
    AVIS_PAIEMENT_EMIS: 'AVIS_PAIEMENT_EMIS',
    /** Un règlement est imputé sans solder l'avis (US-043, US-044). */
    PAIEMENT_ENREGISTRE: 'PAIEMENT_ENREGISTRE',
    /** L'avis est acquitté : la signature n'est plus bloquée par la redevance. */
    PAIEMENT_SOLDE: 'PAIEMENT_SOLDE',
    /** L'échéance est passée : l'avis n'est plus encaissable, il faut réémettre. */
    AVIS_PAIEMENT_ECHU: 'AVIS_PAIEMENT_ECHU',
    /**
     * L'assistance attend une précision du demandeur (US-060).
     *
     * C'est le seul moment où un ticket **bloque sur l'usager** : sans message,
     * il croit son incident pris en charge et le ticket dort jusqu'à sa
     * fermeture automatique.
     */
    TICKET_REPONSE_ATTENDUE: 'TICKET_REPONSE_ATTENDUE',
    /** L'incident est traité : le demandeur a une réponse à lire (US-061). */
    TICKET_RESOLU: 'TICKET_RESOLU',
};
export const CODES_EVENEMENT_NOTIFICATION = valeurs(CODE_EVENEMENT_NOTIFICATION);
// -----------------------------------------------------------------------------
// M-ASSIST — assistance IA, périmètre documentaire strict
// -----------------------------------------------------------------------------
/** `BASE_CONNAISSANCE.type_source`. */
export const TYPE_SOURCE_CONNAISSANCE = {
    GUIDE: 'guide',
    PROCEDURE: 'procedure',
    TEXTE_REGLEMENTAIRE: 'texte_reglementaire',
    FAQ: 'faq',
    TUTORIEL: 'tutoriel',
};
export const TYPES_SOURCE_CONNAISSANCE = valeurs(TYPE_SOURCE_CONNAISSANCE);
/** `CONVERSATION_ASSISTANCE.statut`. */
export const STATUT_CONVERSATION = {
    OUVERTE: 'ouverte',
    CLOSE: 'close',
    ESCALADEE: 'escaladee',
};
export const STATUTS_CONVERSATION = valeurs(STATUT_CONVERSATION);
/** `MESSAGE_ASSISTANCE.role`. */
export const ROLE_MESSAGE_ASSISTANCE = {
    UTILISATEUR: 'utilisateur',
    ASSISTANT: 'assistant',
};
export const ROLES_MESSAGE_ASSISTANCE = valeurs(ROLE_MESSAGE_ASSISTANCE);
// -----------------------------------------------------------------------------
// M-TICKET — incidents utilisateurs
// -----------------------------------------------------------------------------
/** `CATEGORIE_TICKET.code` — les 8 catégories pré-chargées. */
export const CODE_CATEGORIE_TICKET = {
    CONNEXION: 'CONNEXION',
    DONNEE_ERRONEE: 'DONNEE_ERRONEE',
    BLOCAGE_DOSSIER: 'BLOCAGE_DOSSIER',
    PAIEMENT: 'PAIEMENT',
    DOCUMENT: 'DOCUMENT',
    ANOMALIE: 'ANOMALIE',
    DEMANDE_AIDE: 'DEMANDE_AIDE',
    AUTRE: 'AUTRE',
};
export const CODES_CATEGORIE_TICKET = valeurs(CODE_CATEGORIE_TICKET);
/** `TICKET.priorite`. */
export const PRIORITE_TICKET = {
    BASSE: 'basse',
    NORMALE: 'normale',
    HAUTE: 'haute',
    CRITIQUE: 'critique',
};
export const PRIORITES_TICKET = valeurs(PRIORITE_TICKET);
/** `TICKET.statut`. */
export const STATUT_TICKET = {
    NOUVEAU: 'nouveau',
    EN_COURS: 'en_cours',
    EN_ATTENTE_DEMANDEUR: 'en_attente_demandeur',
    RESOLU: 'resolu',
    FERME: 'ferme',
    REJETE: 'rejete',
};
export const STATUTS_TICKET = valeurs(STATUT_TICKET);
/** Bornes de `TICKET.satisfaction` (`CHECK … BETWEEN 1 AND 5`). */
export const SATISFACTION_MIN = 1;
export const SATISFACTION_MAX = 5;
//# sourceMappingURL=transverses.js.map